import { useMemo } from "react";
import { Link } from "react-router";

import { useGo } from "@refinedev/core";
import { ColumnDef } from "@tanstack/react-table";
import { CheckIcon, XIcon } from "lucide-react";

import { ColoredBadge } from "@/components/common/colored-badge";
import {
  embeddingStatusColumn,
  enrichmentStatusColumn,
} from "@/components/common/status-columns";
import { UserAvatar } from "@/components/common/user-avatar";
import { ZoomablePhoto } from "@/components/media/photo-zoomable";
import { CloneButton } from "@/components/refine-ui/buttons/clone";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { makeTooltip } from "@/components/ui/tooltip";
import { useMembers } from "@/contexts/member-context";
import { parseUnixTimestamp } from "@/lib/dateUtils";
import { ConfidenceMeter } from "@/pages/events/components/confidence-meter";
import { PatchInfoModal } from "@/pages/events/features/patch-management/patch-info-modal";
import { IEventData } from "@/types/event";
import type { EventGroupRef } from "@/types/event-group";

export const useEventsColumns = () => {
  const { members } = useMembers();
  const go = useGo();
  const columns = useMemo<ColumnDef<IEventData>[]>(
    () => [
      {
        id: "artwork",
        accessorFn: (row) => row.document.media?.photos?.[0],
        size: 56,
        header: "Art",
        cell: ({ getValue }) => {
          const p: any = getValue();
          if (!p) return <></>;
          return (
            <ZoomablePhoto
              key={p.url}
              url={p.url}
              parentClassName="h-10 w-10"
            />
          );
        },
        enableSorting: false,
        enablePinning: false,
        enableColumnFilter: false,
      },
      {
        id: "name",
        // accessorKey: "document.name",
        accessorFn: (row) =>
          row.highlights.filter((h: any) => h.field === "name")[0]?.snippet ??
          row.document.name,
        header: "Name",
        minSize: 200,
        cell: function render({ getValue }: any) {
          return makeTooltip(
            <div
              className="truncate font-extrabold text-foreground/80"
              dangerouslySetInnerHTML={{ __html: getValue() }}
            ></div>,
          );
        },
        enableSorting: false,
        size: 300,
      },
      {
        id: "startDate",
        header: "Start Date",
        size: 175,
        accessorFn: (row) =>
          parseUnixTimestamp(row.document.startDate, row.document.timezone),
        cell: function render({ getValue, row }: any) {
          const timezone = row.original.document.timezone;
          return makeTooltip(
            <div className="flex flex-col leading-tight">
              <span>{getValue().format("DD/MM/YYYY HH:mm")}</span>
              <span className="text-[10px] text-muted-foreground">
                {timezone ?? "no timezone"}
              </span>
            </div>,
            getValue().format("ddd, MMM D, YYYY HH:mm"),
          );
        },
        enableColumnFilter: true,
        meta: {
          filterType: "date",
        },
      },
      {
        id: "endDate",
        header: "End Date",
        size: 175,
        accessorFn: (row) =>
          row.document.endDate
            ? parseUnixTimestamp(row.document.endDate, row.document.timezone)
            : "",
        cell: function render({ getValue }: any) {
          if (!getValue()) return null;
          return makeTooltip(
            getValue().format("DD/MM/YYYY HH:mm"),
            getValue().format("ddd, MMM D, YYYY HH:mm"),
          );
        },
        meta: {
          filterType: "date",
        },
      },
      {
        id: "location",
        accessorFn: (row) => row.document.venue?.name ?? row.document.mapsUrl,
        header: "Location",
        cell: function render({ getValue }: any) {
          if (URL.canParse(getValue()))
            return (
              <a className="text-blue-500" href={getValue()} target="_blank">
                See in Maps
              </a>
            );
          return makeTooltip(
            <div className="font-extrabold truncate">{getValue()}</div>,
          );
        },
        enableSorting: false,
      },
      {
        id: "group",
        accessorFn: (row) => row.document.group ?? null,
        header: "Group",
        cell: function render({ getValue }: any) {
          const group = getValue() as EventGroupRef | null;
          // A ref with no id has nothing to open: it reads as no group.
          if (!group?.id)
            return <span className="text-muted-foreground">—</span>;
          return (
            <div className="truncate">
              <Link
                className="font-extrabold text-blue-500 hover:underline"
                to={`/event-groups/show/${encodeURIComponent(group.id)}`}
              >
                {group.name || "Group"}
              </Link>
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "offers.type",
        accessorFn: (row) =>
          row.document.offers?.length > 1
            ? row.document.offers?.filter(
                (o) =>
                  parseUnixTimestamp(o.expiresAt) >=
                  parseUnixTimestamp(Date.now()),
              )
            : row.document.offers?.[0],
        header: "Offer",
        cell: function render({ getValue }: any) {
          const offer = getValue();

          if (!offer) {
            return <div className="font-bold">No offer</div>;
          }

          let render: React.ReactElement | null = null;
          if (offer.type === "discount") {
            render = (
              <div className="flex flex-row space-x-1">
                {offer.discount * 100}%
              </div>
            );
          } else if (offer.type === "giveaway") {
            render = (
              <div className="flex flex-row space-x-1">
                {parseUnixTimestamp(offer.expiresAt).format("DD/MM/YYYY HH:mm")}
                -<div className="text-green-700">{offer.status}</div>
              </div>
            );
          }
          return makeTooltip(<div className="truncate">{render}</div>);
        },
        enableSorting: false,
        meta: {
          filterType: "select",
          options: [
            { label: "Giveaway", value: "giveaway" },
            { label: "Discount", value: "discount" },
          ],
        },
      },
      {
        id: "geodata.name",
        accessorFn: (row) =>
          `${row?.document?.geodata?.name}, ${row?.document?.geodata?.country}`,
        cell: ColoredBadge,
        header: "City",
        minSize: 220,
        meta: {
          filterType: "faceted-select",
          optionAccessorFn: (x: any) => {
            return {
              label: x.parent
                ? `${x.parent?.name} (${x.parent?.countryCode})`
                : x.name,
              value: x.value,
            };
          },
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "document.status",
        cell: function render({ getValue }: any) {
          return (
            <ColoredBadge
              getValue={getValue}
              colors={{
                Complete: "bg-green-100 dark:bg-green-900 dark:text-foreground",
                Draft: "bg-gray-100 dark:bg-gray-800 dark:text-gray-300",
              }}
            />
          );
        },
        enableSorting: false,
        size: 125,
        meta: {
          filterType: "select",
          options: [
            {
              label: "Complete",
              value: "Complete",
            },
            { label: "Draft", value: "Draft" },
          ],
        },
      },
      // Enrichment + embedding, immediately after Status — the shared
      // definitions, identical on all nine entity lists.
      enrichmentStatusColumn<IEventData>({ size: 125 }),
      embeddingStatusColumn<IEventData>({ size: 125 }),
      {
        id: "type",
        accessorKey: "document.type",
        header: "Type",
        cell: ColoredBadge,
        enableSorting: false,
        size: 100,
      },
      {
        id: "tags",
        accessorKey: "document.tags",
        header: "Tags",
        enableSorting: false,
        size: 100,
      },
      {
        id: "genres.name",
        accessorFn: (row) =>
          row?.document?.genres?.map((x) => x.name)?.join(", "),
        cell: function render({ getValue }: any) {
          return makeTooltip(
            <div className="font-bold truncate">{getValue()}</div>,
          );
        },
        header: "Genres",
        enableSorting: false,
      },
      {
        id: "interestedIn",
        accessorFn: (row) => row?.document?.interestedIn,
        header: "Interested",
        size: 120,
      },
      {
        id: "lineup.name",
        // accessorKey: "document.lineup",
        accessorFn: (row) => row?.document?.lineup ?? "No lineup",
        cell: function render({ getValue }: string | any) {
          let value = getValue();

          if (Array.isArray(value)) {
            return makeTooltip(
              <div className="flex flex-row space-x-1 truncate">
                {value.map((x, idx) => (
                  <a
                    key={idx}
                    className={x.id ? "font-bold" : ""}
                    href={x.id ? `/artists/${x.id}` : undefined}
                  >
                    {x.name}
                    {idx !== value.length - 1 ? "," : ""}{" "}
                  </a>
                ))}
              </div>,
            );
          }
          return makeTooltip(<div className="truncate">{getValue()}</div>);
        },
        header: "Lineup",
        maxSize: 200,
        enableSorting: false,
      },
      {
        id: "hasTickets",
        accessorFn: (row) => (row?.document?.tickets ?? [])?.length > 0,
        header: "Tickets",
        maxSize: 100,
        cell: function render({ getValue }: any) {
          return getValue() ? (
            <CheckIcon className="text-green-700" />
          ) : (
            <XIcon className="text-red-700" />
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "hasTeasers",
        accessorFn: (row) => (row?.document?.media?.teasers ?? [])?.length > 0,
        header: "Teasers",
        maxSize: 100,
        cell: function render({ getValue }: any) {
          return getValue() ? (
            <CheckIcon className="text-green-700" />
          ) : (
            <XIcon className="text-red-700" />
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "metadata.confidence",
        accessorFn: (row) => row.document.metadata?.confidence,
        header: "Confidence",
        size: 110,
        cell: function render({ getValue }: any) {
          return <ConfidenceMeter value={getValue() as number | undefined} />;
        },
        enableSorting: true,
        meta: {
          filterType: "number",
        },
      },
      {
        // Pipeline popularity, 1-100 for the event's own market. Sorting a
        // market by this column is how the scores get audited, so an unscored
        // event must read as unscored — an em-dash — rather than as a low score.
        id: "metadata.popularity",
        accessorFn: (row) => row?.document?.metadata?.popularity,
        header: "Popularity",
        size: 110,
        cell: function render({ getValue }: any) {
          const value = getValue() as number | undefined | null;
          if (value == null || Number.isNaN(value)) {
            return <span className="text-muted-foreground/60 text-xs">—</span>;
          }
          return (
            <span className="tabular-nums text-[12px] font-semibold tracking-tight">
              {value}
            </span>
          );
        },
        enableSorting: true,
        meta: {
          filterType: "number",
        },
      },
      {
        id: "createdBy",
        header: "Created By",
        minSize: 170,
        accessorKey: "document.createdBy",
        cell: function render({ getValue }: any) {
          let value = getValue();
          const member = members.find(
            (x) =>
              x.id === value || x.adminMetadata?.relatedIds?.includes(value),
          );

          return member ? (
            <div className="p-1">
              <UserAvatar {...member} half />
            </div>
          ) : (
            value
          );
        },
        meta: {
          filterType: "select",
          options: members.map((m) => ({
            value: m.id,
            label: m.displayName,
            // icon: m.photoUrl,
          })),
        },
      },
      {
        id: "createdAt",
        header: "Created At",
        accessorFn: (row) => parseUnixTimestamp(row.document.createdAt),
        cell: function render({ getValue }: any) {
          return makeTooltip(
            getValue().format("DD/MM/YYYY HH:mm"),
            getValue().format("ddd, MMM D, YYYY HH:mm"),
          );
        },
        enableColumnFilter: true,
        meta: {
          filterType: "date",
        },
      },
      {
        // Actions column with edit/delete/show buttons
        id: "actions",
        size: 140,
        header: "Actions",
        cell: ({ row }) => (
          <>
            <EditButton
              size="sm"
              recordItemId={row.original.document.id}
              hideText
              newTab
              onClick={(e) => {
                if (row.original.document.status === "Draft") {
                  e.stopPropagation();
                  go({
                    to: `/events/create`,
                    query: {
                      id: row.original.document.id,
                    },
                  });
                } else {
                  go({
                    to: {
                      resource: "events",
                      id: row.original.document.id,
                      action: "edit",
                    },
                  });
                }
              }}
            />
            <CloneButton
              size="sm"
              recordItemId={row.original.document.id}
              hideText
              newTab
              onClick={(e) => {
                e.stopPropagation();
                const url = go({
                  to: `/events/create`,
                  query: {
                    id: row.original.document.id,
                    clone: true,
                  },
                  type: "path",
                });
                window.open(url!, "_blank");
              }}
            />
            <PatchInfoModal
              id={row.original.document.id as string}
              name={row.original.document.name}
              tags={row.original.document.tags}
            />
          </>
        ),
        enableSorting: false, // Disable sorting for action buttons
        enableColumnFilter: false, // Disable filtering for action buttons
      },
    ],
    [members],
  );

  return columns;
};
